import { useState, useEffect, useRef } from 'react'
import {
  CurecallButton,
  CurecallInput,
  ECurecallButtonAppearance,
  ECurecallSize,
} from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { useSession, APP_NAME } from 'src/providers/auth.provider'
import { TTwoFactorMethod } from 'src/types/two-factor'
import { config } from 'src/config'

const TOKEN_TTL_MS = 5 * 60 * 1000

function getFormattedUrl(path: string): string {
  const removeSlashAtEnd = /\/?\/$/
  const removeSlashAtBeginning = /^\/?\//
  const baseUrl = import.meta.env.DEV ? '/v2' : config.API_URL?.replace(removeSlashAtEnd, '') || '/v2'
  return `${baseUrl}/${path.replace(removeSlashAtBeginning, '')}`
}

type Props = {
  twoFactorMethod: TTwoFactorMethod
  twoFactorToken: string
  onSuccess: () => void
  onCancel: () => void
}

export default function TwoFactorVerification({
  twoFactorMethod,
  twoFactorToken,
  onSuccess,
  onCancel,
}: Props) {
  const { t } = useTranslation()
  const { verifyTwoFactor } = useSession()

  const [code, setCode] = useState('')
  const [isBackupCode, setIsBackupCode] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isLocked, setIsLocked] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [isExpired, setIsExpired] = useState(false)
  const expiryTimeRef = useRef(Date.now() + TOKEN_TTL_MS)

  useEffect(() => {
    const interval = setInterval(() => {
      if (Date.now() >= expiryTimeRef.current) {
        setIsExpired(true)
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!verifyTwoFactor || isLocked || isExpired || !code.trim()) return

    setError(null)
    setIsSubmitting(true)

    try {
      await verifyTwoFactor(twoFactorToken, code.trim())
      onSuccess()
    } catch (err) {
      const message = (err as Error).message
      if (message === 'TWO_FACTOR_LOCKED') {
        setIsLocked(true)
        setError(t('pages.auth.two-factor.error.locked'))
      } else {
        setError(t('pages.auth.two-factor.error.invalid-code'))
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleResendSms = async () => {
    setIsResending(true)
    try {
      const res = await fetch(getFormattedUrl('/auth/2fa/resend-sms'), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'x-client-app': APP_NAME,
        },
        body: JSON.stringify({ twoFactorToken }),
      })

      if (res.status === 403 || res.status === 429) {
        setError(t('pages.auth.two-factor.error.resend-limit'))
      } else if (!res.ok) {
        setError(t('pages.auth.two-factor.error.resend-failed'))
      } else {
        expiryTimeRef.current = Date.now() + TOKEN_TTL_MS
        setIsExpired(false)
      }
    } catch {
      setError(t('pages.auth.two-factor.error.resend-failed'))
    } finally {
      setIsResending(false)
    }
  }

  if (isExpired) {
    return (
      <section className="c-flex c-flex-col c-gap-6 c-items-center">
        <h2 className="c-font-semibold">{t('pages.auth.two-factor.title')}</h2>
        <p className="c-text-danger-500">{t('pages.auth.two-factor.error.expired')}</p>
        <CurecallButton onClick={onCancel} size={ECurecallSize.LARGE}>
          {t('pages.auth.two-factor.back-to-login')}
        </CurecallButton>
      </section>
    )
  }

  return (
    <section className="c-flex c-flex-col c-gap-8 c-items-center">
      <h2 className="c-font-semibold">{t('pages.auth.two-factor.title')}</h2>
      <p className="c-text-center c-text-gray-600 c-max-w-md">
        {isBackupCode
          ? t('pages.auth.two-factor.backup-code-description')
          : twoFactorMethod === 'totp'
            ? t('pages.auth.two-factor.totp-description')
            : t('pages.auth.two-factor.sms-description')}
      </p>

      <form onSubmit={handleSubmit} className="c-w-3/4 c-flex c-flex-col c-gap-6 c-items-center">
        <CurecallInput
          label={
            isBackupCode
              ? t('pages.auth.two-factor.backup-code-label')
              : t('pages.auth.two-factor.code-label')
          }
          value={code}
          onChange={(e) => {
            setCode(e.target.value)
            setError(null)
          }}
          name="code"
          inputMode={isBackupCode ? 'text' : 'numeric'}
          placeholder={isBackupCode ? 'A3F4B2C1' : '000000'}
          errorMessage={error ?? undefined}
          maxLength={isBackupCode ? 8 : 6}
          autoComplete="one-time-code"
          autoFocus
        />

        <CurecallButton
          type="submit"
          size={ECurecallSize.LARGE}
          isLoading={isSubmitting}
          isDisabled={isLocked}
          className="c-w-full"
        >
          {t('pages.auth.two-factor.verify')}
        </CurecallButton>

        {twoFactorMethod === 'sms' && !isBackupCode && (
          <CurecallButton
            appearance={ECurecallButtonAppearance.SECONDARY}
            onClick={handleResendSms}
            isLoading={isResending}
            isDisabled={isLocked}
            type="button"
          >
            {t('pages.auth.two-factor.resend-sms')}
          </CurecallButton>
        )}

        <button
          type="button"
          onClick={() => {
            setIsBackupCode(!isBackupCode)
            setCode('')
            setError(null)
          }}
          className="c-text-primary hover:c-text-primary-700 c-text-sm c-font-medium"
        >
          {isBackupCode
            ? t('pages.auth.two-factor.use-regular-code')
            : t('pages.auth.two-factor.use-backup-code')}
        </button>

        <CurecallButton
          appearance={ECurecallButtonAppearance.CANCEL}
          onClick={onCancel}
          type="button"
          data-1p-ignore
        >
          {t('pages.auth.two-factor.back-to-login')}
        </CurecallButton>
      </form>
    </section>
  )
}
